// Prev/Next through this session's digs (SPEC F6 dig algorithm, step 6).
// Prev and Next move a cursor; Dig always appends at the end.

export interface Session<T> {
  items: readonly T[];
  /** Index of the record on screen; -1 before the first dig */
  cursor: number;
}

export const emptySession = <T>(): Session<T> => ({ items: [], cursor: -1 });

export const current = <T>(s: Session<T>): T | undefined => s.items[s.cursor];
export const canPrev = <T>(s: Session<T>): boolean => s.cursor > 0;
export const canNext = <T>(s: Session<T>): boolean => s.cursor < s.items.length - 1;

/** A new dig: appended after everything already dug, and shown. */
export const push = <T>(s: Session<T>, item: T): Session<T> => ({ items: [...s.items, item], cursor: s.items.length });

export const prev = <T>(s: Session<T>): Session<T> => (canPrev(s) ? { ...s, cursor: s.cursor - 1 } : s);
export const next = <T>(s: Session<T>): Session<T> => (canNext(s) ? { ...s, cursor: s.cursor + 1 } : s);

/** A record that turned out bad (error 100/101/150) leaves the session, so Prev never returns to it. */
export function drop<T>(s: Session<T>, matches: (item: T) => boolean): Session<T> {
  const items = s.items.filter((i) => !matches(i));
  const removedUpToCursor = s.items.slice(0, s.cursor + 1).filter(matches).length;
  return { items, cursor: items.length === 0 ? -1 : Math.max(0, s.cursor - removedUpToCursor) };
}
